import { useState } from "react";
import { LayoutDashboard, ListTree, FileText, ArrowLeft, Search, Users, BookOpen, GraduationCap } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { ColumnDef } from "@tanstack/react-table";
import { EmptyState } from "@shared-kernel/primary/shared/layout/state-components/empty-state";
import { ErrorState } from "@shared-kernel/primary/shared/layout/state-components/error-state";
import { LoadingState } from "@shared-kernel/primary/shared/layout/state-components/loading-state";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import { Input } from "@components/ui/input";
import { DataTable } from "@components/ui/data-table";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { useToast } from "@components/ui/use-toast";
import { MultiSelectFilter } from "@components/ui/multi-select-filter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/ui/alert-dialog";
import { Stepper } from "@shared-kernel/primary/shared/stepper/stepper";
import { ShowcaseBlock } from "@shared-kernel/primary/shared/design-system/shared/showcase-block";

const LAYOUTS = [
  {
    name: "DashboardPageLayout",
    description: "Pages de tableau de bord avec stats / cartes. Pas de back navigation.",
    usage: "Dashboard étudiant / professeur / admin.",
  },
  {
    name: "DataTablePageLayout",
    description: "Pages liste avec DataTable. Gère chargement / erreur / vide.",
    usage: "Liste élèves, paiements, factures…",
  },
  {
    name: "CardListPageLayout",
    description: "Pages liste avec CardList (au lieu d'une table).",
    usage: "Liste cours, masterclasses…",
  },
  {
    name: "CardListWithHeaderPageLayout",
    description: "Comme CardListPageLayout avec en-tête fixe (titre + actions).",
    usage: "Suivi qualité, formations.",
  },
  {
    name: "DetailPageLayout",
    description: "Pages détail / édition. Inclut un BackButton.",
    usage: "Détail élève, professeur, masterclass.",
  },
  {
    name: "DataFormPageLayout / FormPageLayout",
    description: "Pages formulaire — variantes selon la présence de données préchargées.",
    usage: "Création / édition d'entité.",
  },
];

type Student = {
  id: string;
  name: string;
  email: string;
  status: "active" | "pending" | "archived";
};

const SAMPLE_STUDENTS: Student[] = [
  { id: "1", name: "Marie Dubois", email: "marie.dubois@example.com", status: "active" },
  { id: "2", name: "Jean Martin", email: "jean.martin@example.com", status: "pending" },
  { id: "3", name: "Sophie Laurent", email: "sophie.laurent@example.com", status: "active" },
  { id: "4", name: "Paul Durand", email: "paul.durand@example.com", status: "archived" },
];

const STATUS_VARIANTS: Record<Student["status"], "success" | "warning" | "secondary"> = {
  active: "success",
  pending: "warning",
  archived: "secondary",
};

const studentColumns: ColumnDef<Student>[] = [
  { accessorKey: "name", header: "Nom" },
  { accessorKey: "email", header: "Email" },
  {
    accessorKey: "status",
    header: "Statut",
    cell: ({ row }) => <Badge variant={STATUS_VARIANTS[row.original.status]}>{row.original.status}</Badge>,
  },
];

type DemoFormValues = {
  name: string;
  email: string;
};

const demoFormSchema = yup.object({
  name: yup.string().required("Le nom est requis").min(2, "Au moins 2 caractères"),
  email: yup.string().required("L'email est requis").email("Email invalide"),
});

const DemoForm = () => {
  const { toast } = useToast();
  const form = useForm<DemoFormValues>({
    resolver: yupResolver(demoFormSchema),
    defaultValues: { name: "", email: "" },
  });

  const handleSubmit = (values: DemoFormValues) => {
    toast({ description: `Soumis : ${values.name} (${values.email})` });
    form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom</FormLabel>
              <FormControl>
                <Input placeholder="Marie Dubois" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="marie@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2">
          <Button type="submit">Envoyer</Button>
          <Button type="button" variant="outline" onClick={() => form.reset()}>
            Réinitialiser
          </Button>
        </div>
      </form>
    </Form>
  );
};

const FILTER_OPTIONS = [
  { label: "Élève", value: "student" },
  { label: "Professeur", value: "teacher" },
  { label: "Partenaire", value: "provider" },
];

const MULTI_STEP_STEPS = [
  { id: 0, label: "Identité", title: "Identité", Icon: Users },
  { id: 1, label: "Formation", title: "Formation", Icon: BookOpen },
  { id: 2, label: "Validation", title: "Validation", Icon: GraduationCap },
];

const MULTI_STEP_PANELS = [
  { title: "Identité", description: "Nom, prénom, email, téléphone." },
  { title: "Formation", description: "Choix de la formation et de la session." },
  { title: "Validation", description: "Récapitulatif et signature électronique." },
];

const FilterBar = () => {
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<Set<string>>(new Set());

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-3">
      <div className="relative min-w-[220px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-9" />
      </div>
      <MultiSelectFilter title="Rôle" options={FILTER_OPTIONS} selectedValues={roles} onSelectedValuesChange={setRoles} />
      <MultiSelectFilter
        title="Statut"
        options={[
          { label: "Actif", value: "active" },
          { label: "En attente", value: "pending" },
          { label: "Archivé", value: "archived" },
        ]}
        selectedValues={status}
        onSelectedValuesChange={setStatus}
      />
      {(roles.size > 0 || status.size > 0 || search) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setRoles(new Set());
            setStatus(new Set());
            setSearch("");
          }}
        >
          Réinitialiser
        </Button>
      )}
    </div>
  );
};

const MultiStepForm = () => {
  const [step, setStep] = useState(0);
  const current = MULTI_STEP_PANELS[step] ?? MULTI_STEP_PANELS[0]!;
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-slate-800 p-3">
        <Stepper steps={MULTI_STEP_STEPS} currentStep={step} onStepClick={setStep} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{current.title}</CardTitle>
          <CardDescription>{current.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-between">
          <Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep(Math.max(0, step - 1))}>
            Précédent
          </Button>
          <Button
            size="sm"
            disabled={step === MULTI_STEP_STEPS.length - 1}
            onClick={() => setStep(Math.min(MULTI_STEP_STEPS.length - 1, step + 1))}
          >
            Suivant
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const PatternsSection = () => {
  return (
    <div className="space-y-8">
      <ShowcaseBlock
        id="page-layouts"
        title="Page Layouts"
        description="Layouts spécialisés gérant les états loading / error / empty de manière homogène. Importer depuis @shared-kernel/primary/shared/layout/*."
      >
        <div className="grid gap-3 md:grid-cols-2">
          {LAYOUTS.map(layout => (
            <Card key={layout.name}>
              <CardHeader>
                <CardTitle className="text-base">{layout.name}</CardTitle>
                <CardDescription>{layout.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">{layout.usage}</CardContent>
            </Card>
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="empty-state"
        title="État vide (EmptyState)"
        description="Rendu standard quand une page n'a pas de données."
        importPath={`import { EmptyState } from "@shared-kernel/primary/shared/layout/state-components/empty-state";`}
        sourcePath="src/shared-kernel/primary/shared/layout/state-components/empty-state.tsx"
      >
        <div className="rounded-md border bg-background">
          <EmptyState
            icon={<ListTree />}
            title="Aucune masterclass"
            description="Vous n'avez encore créé aucune masterclass."
            cta="Créer une masterclass"
          />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="error-state"
        title="État erreur (ErrorState)"
        importPath={`import { ErrorState } from "@shared-kernel/primary/shared/layout/state-components/error-state";`}
        sourcePath="src/shared-kernel/primary/shared/layout/state-components/error-state.tsx"
      >
        <div className="rounded-md border bg-background">
          <ErrorState title="Erreur de chargement" description="Impossible de récupérer les données. Réessayez plus tard." />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="loading-state"
        title="État chargement (LoadingState)"
        importPath={`import { LoadingState } from "@shared-kernel/primary/shared/layout/state-components/loading-state";`}
        sourcePath="src/shared-kernel/primary/shared/layout/state-components/loading-state.tsx"
      >
        <div className="rounded-md border bg-background">
          <LoadingState />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="page-header-type"
        title="Page header type"
        description="Pattern récurrent dans les pages détail : back button + titre + actions."
      >
        <div className="rounded-md border bg-background p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" aria-label="Retour">
                <ArrowLeft />
              </Button>
              <div>
                <h2 className="text-lg font-semibold">Titre de la ressource</h2>
                <p className="text-xs text-muted-foreground">Sous-titre ou métadonnée</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                Action secondaire
              </Button>
              <Button size="sm">Action principale</Button>
            </div>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="data-table"
        title="DataTable"
        description="Tableau standard avec tri, recherche et pagination intégrés. Utilisé dans DataTablePageLayout."
        importPath={`import { DataTable } from "@components/ui/data-table";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/data-table.tsx"
      >
        <DataTable columns={studentColumns} data={SAMPLE_STUDENTS} searchPlaceHolder="Rechercher un élève..." />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="formulaire"
        title="Formulaire (React Hook Form + Yup)"
        description="Pattern de formulaire avec validation. Voir hooks use-[entity]-create/update pour le câblage Redux."
      >
        <DemoForm />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="dashboard-summary-cards"
        title="Dashboard summary cards"
        description="Pattern de cartes stat utilisé dans les dashboards. Composition Card + Lucide icon."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: "Cours à venir", value: "12", icon: <LayoutDashboard /> },
            { label: "Élèves actifs", value: "48", icon: <FileText /> },
            { label: "Heures délivrées", value: "126h", icon: <ListTree /> },
          ].map(stat => (
            <Card key={stat.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
                <span className="text-muted-foreground">{stat.icon}</span>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="barre-de-filtres"
        title="Barre de filtres"
        description="Recherche + facettes — pattern utilisé au-dessus des DataTable."
      >
        <FilterBar />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="formulaire-multi-etapes"
        title="Formulaire multi-étapes"
        description="Stepper + panneau actif + navigation Précédent/Suivant."
      >
        <MultiStepForm />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="confirmation-destructive"
        title="Confirmation destructive"
        description="AlertDialog — bloque la fenêtre jusqu'à confirmation/annulation explicite. Réservé aux actions irréversibles."
      >
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Supprimer l'élève</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l'élève ?</AlertDialogTitle>
              <AlertDialogDescription>
                Toutes les données associées (cours, paiements, attestations) seront supprimées. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction>Supprimer définitivement</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </ShowcaseBlock>
    </div>
  );
};
